/**
 * ImpostorWord.js
 *
 * Main round component for the “Impostor Word” stage of Trust Issues.
 * ------------------------------------------------------------
 * - Fetches room and team data in real time
 * - Displays the discussion timer synced with Firestore
 * - Renders appropriate view based on player's role:
 *      - Impostors: input to guess movie
 *      - Crew: displays their assigned movie
 * - Admin continues only when all impostors have submitted guesses
 * - Moves to results reveal once all submissions are complete
 */

import React, { useState, useEffect } from "react";
import { listenToRoomData, haveAllImpostorsSubmitted, fetchImpostorGameTeams } from "../helpers/impostorWordGameHelpers";
import ImpostorView from "../components/ImpostorView";
import CrewView from "../components/CrewView";
import ResultsView from "../components/ResultsView";
import DiscussionTimer from "./DiscussionStages";
import Button from "../../../components/ui/Button";

const ImpostorWord = ({ room, user, stage, setStage, isAdmin }) => {
    const [roomData, setRoomData] = useState(room);
    const [teams, setTeams] = useState([]);
    const [revealPhase, setRevealPhase] = useState(false);

    /**
     * 🔁 Listen for live updates to room data (impostor guesses, etc.)
     */
    useEffect(() => {
        const unsub = listenToRoomData(room.id, setRoomData);
        return () => unsub && unsub();
    }, [room.id]);

    /**
     * 📥 Fetch team data for this round from Firestore
     */
    useEffect(() => {
        const loadTeams = async () => {
            if (!room?.id) return;
            const fetchedTeams = await fetchImpostorGameTeams(room.id);
            setTeams(fetchedTeams);
        };
        loadTeams();
    }, [room.id]);

    /**
     * 🧠 Derive role + team info for current user
     */
    const myTeam = teams?.find((t) =>
        t.members.some((m) => m.id === user.uid)
    );
    const myWord = myTeam?.movie || "Guess the MOVIE";
    const isImpostor = myTeam?.members.some(
        (m) => m.id === user.uid && m.isImpostor
    );

    /**
     * 🧩 Admin-only continue handler
     * Checks if all impostors have submitted before revealing results
     */
    const handleAdminContinue = () => {
        if (!haveAllImpostorsSubmitted(roomData)) {
            alert("⏳ Waiting for all impostors to submit their guesses...");
            return;
        }
        setRevealPhase(true);
    };

    // Handle loading state
    if (!roomData)
        return (
            <div className="w-full h-screen flex items-center justify-center text-white">
                Loading game data...
            </div>
        );

    return (
        <div className="w-full min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 py-10 text-center overflow-hidden">
            {/* 🕒 Discussion Timer */}
            <DiscussionTimer roomID={room.id} stage={stage} user={user} />

            {/* 🎭 Game Content */}
            {!revealPhase ? (
                <>
                    {/* 👑 Admin-only continue button */}
                    {isAdmin && (
                        <Button
                            onClick={handleAdminContinue}
                            variant="secondary"
                        >
                            Continue
                        </Button>
                    )}
                    {isImpostor ? (
                        <ImpostorView
                            roomID={room.id}
                            user={user}
                            team={myTeam?.team}
                        />
                    ) : (
                        <CrewView movie={myWord} />
                    )}
                </>
            ) : (
                // 🪄 Results Reveal
                <ResultsView
                    words={teams}
                    roomData={roomData}
                    roomID={room.id}
                    stage={stage}
                    setStage={setStage}
                    isAdmin={isAdmin}
                />
            )}
        </div>
    );
};

export default ImpostorWord;